import { Grid, SelectChangeEvent, Typography } from '@mui/material';
import { ProceedSaveLater } from './components';
import {
  Formik,
  FormikHelpers,
  useFormikContext,
  validateYupSchema,
  yupToFormErrors,
} from 'formik';
import { MFTextField } from '../../lib/formik';
import {
  ApplicationProps,
  JointholderRelationMaster,
  DistributorListType,
} from '../../redux-store/types/api-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory, useLocation } from 'react-router';
import { ClassPlanProps, FundProps } from '../../redux-store/types/funds';
import { investmentPaymentDetailsSchema } from '../../utils/schema';
import {
  APPLICATION_TYPE,
  RiskProfileEnabled,
  SAVE_LATER_VALIDATION_CHECK,
  USER_ROLES,
  defaultCurrency,
} from '../../utils/constant';
import {
  allowOnlyNumbers,
  applicationComparison,
  checkIfOtherSetupFeeApplicableReqd,
  currencyConversion,
  getCurrentStep,
  getStep,
  isAccreditedInvestorApplicable,
  isFieldApplicable,
  isFieldTypeTabular,
  isFieldValueFixed,
  isFieldValueInWords,
  isFieldVariable,
  navigateToNextStep,
  saveForLater,
  setUpFeeCalculations,
  statusRoleBased,
  updatedCommitmentAmount,
} from '../../utils/utilityFunctions';
import { useSnackbar } from 'notistack';
import { getDirectApplicableFunds } from '../../redux-store/actions/funds';
import { getDistributorById } from '../../redux-store/actions/userManagement';
import { SearchableSelect } from '../../lib/formik/searchSelectField';
import { useMdmsJointHolderRelationsData } from '../../utils/useDataMdms';
import FieldValidationNote from './FieldValidationNote';
import MFCheckbox from '../../lib/formik/Checkbox';
import { KeyValue } from '../ApplicationDetails/contributor';
import { ExitLoadDetails } from '../ApplicationDetails/ExitLoadDetails';
import { ConfirmationDialog } from '../commonComponents';
import { useFeatureToggles } from '../../utils/useDataMdms';

type Values = Partial<ApplicationProps> & {
  saveType: string;
};

const fundInitialValues = {
  schemeId: '',
  planId: '',
  commitmentAmount: 0,
  aggregateAmount: 0,
  topupAmount: 0,
  setupFeePercentage: 0,
  managementFeesRate: 0,
  taxRate: 0,
  drawDownNumber: 0,
  contributionChequeAmount: 0,
  setupFeeAmount: null,
  setupFeeGST: null,
  totalSetupFee: null,
  setupFeeTDS: null,
  netSetupFeeLessTDS: null,
  totalAmountPayable: null,
  isAccreditedInvestor: false,
  hurdleRateApplicable: '',
  hurdleRate: 0,
  performanceFeeApplicable: '',
  performanceFee: 0,
  exitChargeApplicable: '',
  exitCharge: 0,
  managementFeeApplicable: '',
  exitChargeType: '',
  hurdleRateType: '',
  performanceFeeType: '',
  managementFeeType: '',
  placementFeeApplicable: '',
  placementFeeType: '',
  placementFee: 0,
  operatingExpenseApplicable: '',
  operatingExpenses: 0,
  operatingExpenseType: '',
  setupFeeType: '',
  setupFeeApplicable: '',
  setupFeeInWords: '',
  otherSetUpFeeApplicable: '',
  transactionCurrency: defaultCurrency,
  exitLoad: '',
  additionalReturnApplicable: '',
  additionalReturnType: '',
  defaultAdditionalReturn: 0,
  exitLoadDetails: [],
  exitLoadDetailsApplicable: '',
  exitLoadDetailsType: '',
  operatingExpenseInWords: '',
  carryShareApplicable: '',
  carryShareType: '',
  carryShare: null,
  contributionAmtApplicable: '',
};

const initialValues: Values = {
  ...fundInitialValues,
  modeOfPayment: 'cheque',
  paymentRefNo: '',
  chequeDate: '',
  paymentBankAccountNumber: '',
  paymentBankame: '',
  paymentBankAccontType: '',
  saveType: 'save and proceed',
};

const removeErrors = () => {
  return {
    isAccreditedInvestor: false,
    schemeId: false,
    planId: false,
    commitmentAmount: false,
    aggregateAmount: false,
    contributionChequeAmount: false,
    setupFeePercentage: false,
    managementFeesRate: false,
    setupFeeAmount: false,
    setupFeeGST: false,
    totalSetupFee: false,
    setupFeeTDS: false,
    netSetupFeeLessTDS: false,
    totalAmountPayable: false,
    hurdleRateApplicable: false,
    hurdleRate: false,
    performanceFeeApplicable: false,
    performanceFee: false,
    exitChargeApplicable: false,
    exitCharge: false,
    managementFeeApplicable: false,
    managementFeeType: false,
    hurdleRateType: false,
    exitChargeType: false,
    performanceFeeType: false,
    placementFee: false,
    placementFeeApplicable: false,
    placementFeeType: false,
    operatingExpenses: false,
    operatingExpenseApplicable: false,
    operatingExpenseType: false,
    setupFeeApplicable: false,
    setupFeeType: false,
    transactionCurrency: false,
  };
};

const InvestmentDetails = ({
  values,
  setSelectedPlan,
  setValues,
  isFieldDisabled,
  getClassPlanOptionsOrDetails,
  selectedFund,
  selectedPlan,
}: {
  values: Values;
  setSelectedPlan: React.Dispatch<React.SetStateAction<Partial<ClassPlanProps> | null>>;
  setValues: FormikHelpers<Values>['setValues'];
  isFieldDisabled: boolean;
  getClassPlanOptionsOrDetails: (
    arg1: boolean,
    arg2?: string
  ) =>
    | string
    | number
    | boolean
    | ClassPlanProps[]
    | {
        key: string;
        value: number;
      }[];
  selectedFund: FundProps | null;
  selectedPlan: Partial<ClassPlanProps> | null;
}) => {
  const { setTouched, touched } = useFormikContext<Values>();
  return (
    <>
      <Grid item xs={12} sm={6}>
        <SearchableSelect
          name={`planId`}
          label={`Class (Plan) *`}
          items={
            getClassPlanOptionsOrDetails(values.isAccreditedInvestor as boolean) as [
              { key: string; value: string | number }
            ]
          }
          disabled={!values.schemeId || isFieldDisabled}
          onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
            const selectedPlan = selectedFund?.plans
              ?.filter((plan) => plan.isActive)
              ?.find((p) => Number(p?.id) === value);
            setSelectedPlan(selectedPlan || null);
            setValues({
              ...values,
              planId: value as string,
              commitmentAmount: 0,
              contributionChequeAmount: 0,
              setupFeePercentage: selectedPlan?.setupFee,
              managementFeesRate: selectedPlan?.defaultManagementFee,
              setupFeeAmount: 0,
              setupFeeGST: 0,
              totalSetupFee: 0,
              setupFeeTDS: 0,
              netSetupFeeLessTDS: 0,
              totalAmountPayable: 0,
              hurdleRateApplicable: selectedPlan?.hurdleRateApplicable,
              hurdleRate: selectedPlan?.defaultHurdleRate || 0,
              performanceFeeApplicable: selectedPlan?.performanceFeeApplicable,
              performanceFee: selectedPlan?.defaultPerformanceFee || 0,
              exitChargeApplicable: selectedPlan?.exitChargeApplicable,
              exitCharge: selectedPlan?.defaultExitCharge || 0,
              managementFeeApplicable: selectedPlan?.managementFeeApplicable,
              managementFeeType: selectedPlan?.managementFeeType,
              hurdleRateType: selectedPlan?.hurdleRateType,
              exitChargeType: selectedPlan?.exitChargeType,
              performanceFeeType: selectedPlan?.performanceFeeType,
              placementFee: selectedPlan?.defaultPlacementFee || 0,
              placementFeeApplicable: selectedPlan?.placementFeeApplicable,
              placementFeeType: selectedPlan?.placementFeeType,
              operatingExpenses: selectedPlan?.defaultOperatingExpenses || 0,
              operatingExpenseApplicable: selectedPlan?.operatingExpenseApplicable,
              operatingExpenseType: selectedPlan?.operatingExpenseType,
              setupFeeApplicable: selectedPlan?.setupFeeApplicable,
              setupFeeType: selectedPlan?.setupFeeType,
              transactionCurrency: selectedPlan?.transactionCurrency,
              exitLoad: selectedPlan?.exitLoad,
              defaultAdditionalReturn: selectedPlan?.defaultAdditionalReturn || 0,
              additionalReturnApplicable: selectedPlan?.additionalReturnApplicable,
              additionalReturnType: selectedPlan?.additionalReturnType,
              exitLoadDetails: selectedPlan?.exitLoadDetails,
              exitLoadDetailsApplicable: selectedPlan?.exitLoadDetailsApplicable,
              exitLoadDetailsType: selectedPlan?.exitLoadDetailsType,
              setupFeeInWords: selectedPlan?.setupFeeInWords || '',
              operatingExpenseInWords: selectedPlan?.operatingExpenseInWords || '',
              otherSetUpFeeApplicable: selectedPlan?.otherSetUpFeeApplicable || '',
              carryShareApplicable: selectedPlan?.carryShareApplicable || '',
              carryShareType: selectedPlan?.carryShareType || '',
              carryShare: selectedPlan?.carryShare,
              contributionAmtApplicable: selectedPlan?.contributionAmtApplicable || '',
            });
            setTouched({
              ...touched,
              ...removeErrors(),
              schemeId: touched.schemeId,
              isAccreditedInvestor: touched.isAccreditedInvestor,
            });
          }}
          searchFieldPlaceholder={'Search Class (Plan)'}
        />
      </Grid>
      <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
        <MFTextField
          name={'commitmentAmount'}
          label={'Commitment Amount *'}
          type="number"
          placeholder="Enter Commitment Amount"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
            const feeAmountObj = setUpFeeCalculations(
              Number(value) || 0,
              values.setupFeePercentage || 0
            );
            const netSetupFee = feeAmountObj.totalSetupFee - (values.setupFeeTDS || 0);
            setValues({
              ...values,
              commitmentAmount: value ? Number(value) : null,
              setupFeeAmount: feeAmountObj.feeAmount,
              setupFeeGST: feeAmountObj.feeGst,
              totalSetupFee: feeAmountObj.totalSetupFee,
              netSetupFeeLessTDS: netSetupFee,
              totalAmountPayable: values.contributionChequeAmount || Number(value),
            });
          }}
          trimOnBlur={false}
          disabled={isFieldDisabled || !values.planId}
        />
        {values.commitmentAmount != 0 && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.commitmentAmount, selectedPlan?.currency)}
          </Typography>
        )}
      </Grid>
      {isFieldApplicable(values?.managementFeeApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'managementFeesRate'}
            label={`Management Fees Rate % *`}
            placeholder="Enter Management Fee Percentage"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(
                values?.managementFeeType || '',
                values?.managementFeeApplicable || ''
              ) ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.placementFeeApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'placementFee'}
            label="Placement Fees Rate % *"
            placeholder="Enter Placement Fee Percentage"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(
                values?.placementFeeType || '',
                values?.placementFeeApplicable || ''
              ) ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {(isFieldVariable(
        values?.operatingExpenseType || '',
        values?.operatingExpenseApplicable || ''
      ) ||
        isFieldValueFixed(
          values?.operatingExpenseType || '',
          values?.operatingExpenseApplicable || ''
        )) && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'operatingExpenses'}
            label="Operating Expense Rate % *"
            placeholder="Enter Operating Expense Percentage"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(
                values?.operatingExpenseType || '',
                values?.operatingExpenseApplicable || ''
              ) ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.contributionAmtApplicable || '') && (
        <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
          <MFTextField
            name={'contributionChequeAmount'}
            label={`Contribution Amount *`}
            placeholder="Enter Contribution Amount"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
              const feeAmountObj = setUpFeeCalculations(
                values.commitmentAmount || 0,
                values.setupFeePercentage || 0
              );
              const netSetupFee = feeAmountObj.totalSetupFee - (values.setupFeeTDS || 0);
              setValues({
                ...values,
                contributionChequeAmount: value ? Number(value) : null,
                setupFeeAmount: feeAmountObj.feeAmount,
                setupFeeGST: feeAmountObj.feeGst,
                totalSetupFee: feeAmountObj.totalSetupFee,
                netSetupFeeLessTDS: netSetupFee,
                totalAmountPayable: Number(value) || values.commitmentAmount,
              });
            }}
            trimOnBlur={false}
            disabled={isFieldDisabled || !values.planId}
          />
          {values.contributionChequeAmount != 0 && (
            <Typography sx={{ fontSize: 13 }}>
              {currencyConversion(values.contributionChequeAmount, selectedPlan?.currency)}
            </Typography>
          )}
        </Grid>
      )}
      {(isFieldVariable(values?.setupFeeType || '', values.setupFeeApplicable || '') ||
        isFieldValueFixed(values?.setupFeeType || '', values.setupFeeApplicable || '')) && (
        <>
          <Grid item xs={12} sm={6}>
            <MFTextField
              name={'setupFeePercentage'}
              label="Setup Fee Percentage % *"
              type="number"
              placeholder="Enter Setup Fee Percentage"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                const feeAmountObj = setUpFeeCalculations(
                  values.commitmentAmount || 0,
                  Number(value)
                );
                const netSetupFee = feeAmountObj.totalSetupFee - (values.setupFeeTDS || 0);
                setValues({
                  ...values,
                  setupFeePercentage: value ? Number(value) : null,
                  setupFeeAmount: feeAmountObj.feeAmount,
                  setupFeeGST: feeAmountObj.feeGst,
                  totalSetupFee: feeAmountObj.totalSetupFee,
                  netSetupFeeLessTDS: netSetupFee,
                  totalAmountPayable: values.contributionChequeAmount || values.commitmentAmount,
                });
              }}
              trimOnBlur={false}
              disabled={
                isFieldDisabled ||
                !isFieldVariable(values?.setupFeeType || '', values?.setupFeeApplicable || '') ||
                !values.planId
              }
            />
          </Grid>

          {isFieldApplicable(values?.otherSetUpFeeApplicable || '') &&
            checkIfOtherSetupFeeApplicableReqd(
              values.setupFeeType || '',
              values?.setupFeeApplicable || ''
            ) && (
              <>
                <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                  <MFTextField
                    name={'setupFeeAmount'}
                    label="Setup Fee Amount"
                    type="number"
                    placeholder="Enter Setup Fee Amount"
                    onKeyDown={(e) => {
                      allowOnlyNumbers(e);
                    }}
                    trimOnBlur={false}
                    disabled={true}
                  />
                  {values.setupFeeAmount != 0 && (
                    <Typography sx={{ fontSize: 13 }}>
                      {currencyConversion(values.setupFeeAmount, selectedPlan?.currency)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                  <MFTextField
                    name={'setupFeeGST'}
                    label="GST (GST On Setup Fee Amount)"
                    type="number"
                    placeholder="Enter GST"
                    onKeyDown={(e) => {
                      allowOnlyNumbers(e);
                    }}
                    trimOnBlur={false}
                    disabled={true}
                  />
                  {values.setupFeeGST != 0 && (
                    <Typography sx={{ fontSize: 13 }}>
                      {currencyConversion(values.setupFeeGST, selectedPlan?.currency)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                  <MFTextField
                    name={'totalSetupFee'}
                    label="Total Setup Fee"
                    type="number"
                    placeholder="Enter Total Setup Fee"
                    onKeyDown={(e) => {
                      allowOnlyNumbers(e);
                    }}
                    trimOnBlur={false}
                    disabled={true}
                  />
                  {values.totalSetupFee != 0 && (
                    <Typography sx={{ fontSize: 13 }}>
                      {currencyConversion(values.totalSetupFee, selectedPlan?.currency)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                  <MFTextField
                    name={'setupFeeTDS'}
                    label="TDS"
                    type="number"
                    placeholder="Enter TDS"
                    onKeyDown={(e) => {
                      allowOnlyNumbers(e);
                    }}
                    onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                      const setupFeeLessTDS = Number(values.totalSetupFee || 0) - Number(value);
                      setValues({
                        ...values,
                        setupFeeTDS: value ? Number(value) : null,
                        netSetupFeeLessTDS: setupFeeLessTDS,
                        totalAmountPayable:
                          values.contributionChequeAmount || values.commitmentAmount,
                      });
                    }}
                    trimOnBlur={false}
                    disabled={isFieldDisabled || !values.planId}
                  />
                  {values.setupFeeTDS != 0 && (
                    <Typography sx={{ fontSize: 13 }}>
                      {currencyConversion(values.setupFeeTDS, selectedPlan?.currency)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
                  <MFTextField
                    name={'netSetupFeeLessTDS'}
                    label="Setup Fee Payable"
                    type="number"
                    placeholder="Enter Setup Fee Payable"
                    onKeyDown={(e) => {
                      allowOnlyNumbers(e);
                    }}
                    trimOnBlur={false}
                    disabled={true}
                  />
                  {values.netSetupFeeLessTDS != 0 && (
                    <Typography sx={{ fontSize: 13 }}>
                      {currencyConversion(values.netSetupFeeLessTDS, selectedPlan?.currency)}
                    </Typography>
                  )}
                </Grid>
              </>
            )}
        </>
      )}
      {isFieldApplicable(values?.hurdleRateApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'hurdleRate'}
            label={`Hurdle Rate % *`}
            placeholder="Enter Hurdle Rate"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(values?.hurdleRateType || '', values?.hurdleRateApplicable || '') ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.performanceFeeApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'performanceFee'}
            label={`Performance Fee % *`}
            placeholder="Enter Performance Fee"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(
                values?.performanceFeeType || '',
                values?.performanceFeeApplicable || ''
              ) ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.exitChargeApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'exitCharge'}
            label={`Exit Charge %`}
            placeholder="Enter Exit Charge"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(values?.exitChargeType || '', values?.exitChargeApplicable || '') ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.additionalReturnApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'defaultAdditionalReturn'}
            label="Additional Return*"
            placeholder="Enter Additional Return"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(
                values?.additionalReturnType || '',
                values?.additionalReturnApplicable || ''
              ) ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      {isFieldApplicable(values?.carryShareApplicable || '') && (
        <Grid item xs={12} sm={6}>
          <MFTextField
            name={'carryShare'}
            label="Carry Share"
            placeholder="Enter Carry Share"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
            disabled={
              !isFieldVariable(values?.carryShareType || '', values?.carryShareApplicable || '') ||
              isFieldDisabled ||
              !values.planId
            }
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} sx={{ '.MuiFormControl-root': { marginBottom: '2px' } }}>
        <MFTextField
          name={'totalAmountPayable'}
          label={'Total Amount Payable'}
          type="number"
          placeholder="Enter Total Amount Payable"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
          disabled={true}
        />
        {values.totalAmountPayable != 0 && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.totalAmountPayable, selectedPlan?.currency)}
          </Typography>
        )}
      </Grid>
      {isFieldValueInWords(
        values?.operatingExpenseType || '',
        values?.operatingExpenseApplicable || ''
      ) && <KeyValue title={'Operating Expense'} description={values.operatingExpenseInWords} />}
      {isFieldValueInWords(values.setupFeeType || '', values?.setupFeeApplicable || '') && (
        <KeyValue title={'Setup Fee'} description={values.setupFeeInWords} />
      )}
      {isFieldApplicable(values.exitLoadDetailsApplicable || '') && (
        <>
          {isFieldTypeTabular(values?.exitLoadDetailsType || '') ? (
            <ExitLoadDetails exitLoadDetails={values.exitLoadDetails || []} />
          ) : (
            <Grid item xs={12} sm={6}>
              <MFTextField
                name={`exitLoad`}
                multiline
                minRows={1}
                maxRows={5}
                label={`Exit Load *`}
                placeholder={`Enter Exit Load`}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default function InvestmentPaymentDetails(): JSX.Element {
  // const [open, setOpen] = useState(false);
  // eslint-disable-next-line
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [fundsList, setFundsList] = useState<FundProps[]>([]);
  const [selectedFund, setSelectedFund] = useState<FundProps | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Partial<ClassPlanProps> | null>(null);
  const { application } = useSelector((store: RootStateType) => store.application);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [paymentDetails, setPaymentDetails] = useState(initialValues);
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [jointHoldersData, setJointHoldersData] = useState<JointholderRelationMaster>();
  const { jointHolderRelations } = useMdmsJointHolderRelationsData();
  const location = useLocation();
  const path = location.pathname;
  const { toggler } = useFeatureToggles();

  const {
    minCommitmentAmount = 10000000,
    maxCommitmentAmount = 20000000,
    minContributionPercentage = 10,
    currency = defaultCurrency,
    maxExitCharge = 100,
    maxHurdleRate = 100,
    maxManagementFee = 100,
    maxPerformanceFee = 100,
    minExitCharge = 0,
    minHurdleRate = 0,
    minManagementFee = 0,
    minPerformanceFee = 0,
    maxPlacementFee = 0,
    maxOperatingExpense = 0,
    minPlacementFee = 0,
    minOperatingExpense = 0,
    minSetupFee = 0,
    maxSetupFee = 0,
    maxAdditionalReturn = 0,
    minAdditionalReturn = 0,
    minCarryShare = 0,
    maxCarryShare = 100,
  } = selectedPlan || {};

  useEffect(() => {
    const { plan } = application || ({} as ApplicationProps);
    const {
      minCommitmentAmount = 1,
      maxCommitmentAmount = 0,
      minContributionPercentage = 1,
      currency = defaultCurrency,
      accreditedMinCommitmentAmount = 0,
      maxExitCharge = 100,
      maxHurdleRate = 100,
      maxManagementFee = 100,
      maxPerformanceFee = 100,
      minExitCharge = 0,
      minHurdleRate = 0,
      minManagementFee = 0,
      minPerformanceFee = 0,
      maxPlacementFee = 100,
      minPlacementFee = 0,
      maxOperatingExpense = 100,
      minOperatingExpense = 0,
      minSetupFee = 0,
      maxSetupFee = 100,
      transactionCurrency = defaultCurrency,
      maxAdditionalReturn = 100,
      minAdditionalReturn = 0,
      minCarryShare = 0,
      maxCarryShare = 0,
    } = plan || {};
    setSelectedPlan(
      plan
        ? {
            ...selectedPlan,
            minCommitmentAmount: Number(minCommitmentAmount),
            maxCommitmentAmount: Number(maxCommitmentAmount),
            minContributionPercentage: Number(minContributionPercentage),
            currency,
            accreditedMinCommitmentAmount: Number(accreditedMinCommitmentAmount),
            maxExitCharge: maxExitCharge === 0 || maxExitCharge ? Number(maxExitCharge) : 100,
            maxHurdleRate: maxHurdleRate === 0 || maxHurdleRate ? Number(maxHurdleRate) : 100,
            maxManagementFee:
              maxManagementFee === 0 || maxManagementFee ? Number(maxManagementFee) : 100,
            maxPerformanceFee:
              maxPerformanceFee === 0 || maxPerformanceFee ? Number(maxPerformanceFee) : 100,
            minExitCharge: Number(minExitCharge),
            minHurdleRate: Number(minHurdleRate),
            minManagementFee: Number(minManagementFee),
            minPerformanceFee: Number(minPerformanceFee),
            maxPlacementFee:
              maxPlacementFee === 0 || maxPlacementFee ? Number(maxPlacementFee) : 100,
            minPlacementFee: Number(minPlacementFee),
            maxOperatingExpense:
              maxOperatingExpense === 0 || maxOperatingExpense ? Number(maxOperatingExpense) : 100,
            minOperatingExpense: Number(minOperatingExpense),
            maxAdditionalReturn:
              maxAdditionalReturn === 0 || maxAdditionalReturn ? Number(maxAdditionalReturn) : 100,
            minAdditionalReturn: Number(minAdditionalReturn),
            maxSetupFee: maxSetupFee === 0 || maxSetupFee ? Number(maxSetupFee) : 100,
            minSetupFee: Number(minSetupFee),
            transactionCurrency: transactionCurrency || defaultCurrency,
            minCarryShare: typeof minCarryShare === 'number' ? minCarryShare : 0,
            maxCarryShare: typeof maxCarryShare === 'number' ? maxCarryShare : 100,
          }
        : null
    );

    setPaymentDetails({
      ...paymentDetails,
      schemeId: application?.schemeId || '',
      planId: application?.planId || '',
      commitmentAmount: application?.commitmentAmount || null,
      setupFeePercentage: application?.setupFeePercentage || 0,
      managementFeesRate: application?.managementFeesRate || 0,
      // taxRate: application?.taxRate || null,
      // drawDownNumber: application?.drawDownNumber || null,
      contributionChequeAmount: Number(application?.contributionChequeAmount) || 0,
      modeOfPayment: application?.modeOfPayment || '',
      paymentRefNo: application?.paymentRefNo || '',
      chequeDate: application?.chequeDate || null,
      paymentBankAccountNumber: application?.paymentBankAccountNumber || '',
      paymentBankame: application?.paymentBankame || '',
      paymentBankAccontType: application?.paymentBankAccontType || '',
      setupFeeAmount: application?.setupFeeAmount,
      setupFeeGST: application?.setupFeeGST,
      totalSetupFee: application?.totalSetupFee,
      setupFeeTDS: application?.setupFeeTDS,
      netSetupFeeLessTDS: application?.netSetupFeeLessTDS,
      totalAmountPayable: application?.totalAmountPayable,
      isAccreditedInvestor: application?.isAccreditedInvestor || false,
      exitChargeApplicable: application?.exitChargeApplicable || '',
      exitCharge: application?.exitCharge || 0,
      hurdleRateApplicable: application?.hurdleRateApplicable || '',
      hurdleRate: application?.hurdleRate || 0,
      performanceFeeApplicable: application?.performanceFeeApplicable || '',
      performanceFee: application?.performanceFee || 0,
      managementFeeApplicable: application?.managementFeeApplicable || '',
      managementFeeType: application?.managementFeeType || '',
      exitChargeType: application?.exitChargeType || '',
      hurdleRateType: application?.hurdleRateType || '',
      performanceFeeType: application?.performanceFeeType || '',
      placementFeeType: application?.placementFeeType || '',
      placementFeeApplicable: application?.placementFeeApplicable || '',
      placementFee: application?.placementFee || 0,
      operatingExpenseType: application?.operatingExpenseType || '',
      operatingExpenseApplicable: application?.operatingExpenseApplicable || '',
      operatingExpenses: application?.operatingExpenses || 0,
      additionalReturnType: application?.additionalReturnType || '',
      additionalReturnApplicable: application?.additionalReturnApplicable || '',
      defaultAdditionalReturn: application?.defaultAdditionalReturn || 0,
      setupFeeType: application?.setupFeeType || '',
      setupFeeApplicable: application?.setupFeeApplicable || '',
      transactionCurrency: application?.transactionCurrency || defaultCurrency,
      exitLoad: application?.exitLoad,
      exitLoadDetails: application?.exitLoadDetails,
      exitLoadDetailsApplicable: application?.exitLoadDetailsApplicable,
      exitLoadDetailsType: application?.exitLoadDetailsType,
      setupFeeInWords: application?.setupFeeInWords || '',
      otherSetUpFeeApplicable: application?.otherSetUpFeeApplicable || '',
      operatingExpenseInWords: application?.operatingExpenseInWords || '',
      carryShareApplicable: application?.carryShareApplicable || '',
      carryShareType: application?.carryShareType || '',
      carryShare: application?.carryShare || 0,
      contributionAmtApplicable: application?.contributionAmtApplicable || '',
    });
  }, [application]);

  const handleSubmit = async (values: Values) => {
    try {
      const {
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationType,
        applicationNumber,
        topupApplicationNumber,
        // eslint-disable-next-line
        topupAmount: existingTopupAmount,
        // eslint-disable-next-line
        aggregateAmount: existingAggregateAmount,
        isTopup,
        ...existingApplicationRest
      } = application || {};
      // eslint-disable-next-line
      const { saveType, topupAmount, aggregateAmount, ...rest } = values;
      const isSaveLater = saveType !== 'save and proceed';

      const updatedApplication = {
        ...existingApplicationRest,
        ...rest,
        id,
        applicant1ReferenceId,
        currentStep,
        applicationType,
        applicationNumber,
        topupApplicationNumber,
        schemeId: rest.schemeId || null,
        planId: rest.planId || null,
        contributionChequeAmount: Number(rest.contributionChequeAmount) || 0,
        // setupFeePercentage: Number(rest.setupFeePercentage),
        managementFeesRate: isFieldApplicable(rest?.managementFeeApplicable || '')
          ? Number(rest.managementFeesRate)
          : null,
        hurdleRate: isFieldApplicable(rest?.hurdleRateApplicable || '')
          ? Number(rest.hurdleRate)
          : null,
        performanceFee: isFieldApplicable(rest?.performanceFeeApplicable || '')
          ? Number(rest.performanceFee)
          : null,
        exitCharge: isFieldApplicable(rest?.exitChargeApplicable || '')
          ? Number(rest.exitCharge)
          : null,
        managementFeeType: rest?.managementFeeType || null,
        exitChargeType: rest?.exitChargeType || null,
        hurdleRateType: rest?.hurdleRateType || null,
        performanceFeeType: rest?.performanceFeeType || null,
        placementFee: isFieldApplicable(rest?.placementFeeApplicable || '')
          ? Number(rest.placementFee)
          : null,
        operatingExpenses: isFieldApplicable(rest?.operatingExpenseApplicable || '')
          ? Number(rest.operatingExpenses)
          : null,
        setupFeePercentage: isFieldApplicable(rest?.setupFeeApplicable || '')
          ? Number(rest.setupFeePercentage)
          : null,
        transactionCurrency: rest?.transactionCurrency || defaultCurrency,
        exitLoad:
          isFieldApplicable(rest?.exitLoadDetailsApplicable || '') &&
          !isFieldTypeTabular(rest?.exitLoadDetailsType || '')
            ? rest.exitLoad
            : '',
        exitLoadDetails:
          isFieldApplicable(rest?.exitLoadDetailsApplicable || '') &&
          isFieldTypeTabular(rest?.exitLoadDetailsType || '')
            ? rest.exitLoadDetails
            : [],
        defaultAdditionalReturn: isFieldApplicable(rest?.additionalReturnApplicable || '')
          ? Number(rest.defaultAdditionalReturn)
          : null,
        setupFeeInWords: isFieldValueInWords(
          rest.setupFeeType || '',
          rest?.setupFeeApplicable || ''
        )
          ? rest?.setupFeeInWords
          : '',
        operatingExpenseInWords: isFieldValueInWords(
          rest.operatingExpenseType || '',
          rest?.operatingExpenseApplicable || ''
        )
          ? rest?.operatingExpenseInWords
          : '',
        carryShareType: rest?.carryShareType || null,
        carryShare: isFieldApplicable(rest?.carryShareApplicable || '')
          ? Number(rest?.carryShare)
          : 0,
      };
      const checkApplication = applicationComparison(
        {
          ...existingApplicationRest,
          id,
          applicant1ReferenceId,
          currentStep,
          applicationType,
          applicationNumber,
          topupApplicationNumber,
          setupFeePercentage: Number(application?.setupFeePercentage),
          managementFeeType: application?.managementFeeType || null,
          exitChargeType: application?.exitChargeType || null,
          hurdleRateType: application?.hurdleRateType || null,
          performanceFeeType: application?.performanceFeeType || null,
          placementFee: Number(application?.placementFee),
          operatingExpenses: Number(application?.operatingExpenses),
          transactionCurrency: application?.transactionCurrency,
        },
        {
          ...updatedApplication,
          currentStep:
            !!currentStep &&
            currentStep >
              getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler
              ) -
                1
              ? currentStep
              : getCurrentStep(
                  application?.applicationType || '',
                  path,
                  !!isTopup,
                  isSaveLater,
                  application?.createdAt,
                  toggler
                ),
        }
      );

      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...updatedApplication,
              status: statusRoleBased(role, application),
              currentStep: getCurrentStep(
                application?.applicationType || '',
                path,
                !!isTopup,
                isSaveLater,
                application?.createdAt,
                toggler
              ),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        history.push(
          navigateToNextStep(
            path,
            role,
            id || '',
            isSaveLater,
            applicant1ReferenceId || '',
            application?.applicationType || '',
            !!isTopup,
            application?.createdAt,
            toggler
          ),
          {
            id,
            applicant1ReferenceId,
            topUpApplication: application?.topupInitiated,
          }
        );
      }
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        const response = application?.distributor_id
          ? ((
              (await dispatch(
                getDistributorById(application?.distributor_id)
              )) as unknown as DistributorListType
            ).planDetails.schemes as FundProps[])
          : ((await dispatch(
              getDirectApplicableFunds({ isActive: true })
            )) as unknown as FundProps[]);
        if (!isComponentAlive) {
          return;
        }
        const { scheme } = application || {};
        const updatedFundPlan =
          application?.schemeId &&
          (!application.scheme.isActive ||
            !(response.map((res) => res?.id?.toString()) as unknown as string[]).includes(
              application?.schemeId?.toString()
            ))
            ? [
                ...response,
                {
                  ...application.scheme,
                  plans: [application.plan],
                } as unknown as FundProps,
              ]
            : response;
        setFundsList(updatedFundPlan);
        const updatePlans =
          application?.schemeId &&
          application?.scheme.isActive &&
          application?.planId &&
          !application.plan?.isActive
            ? ({
                ...updatedFundPlan.find((fund) => Number(fund?.id) === Number(scheme?.id)),
                plans: [
                  ...(
                    updatedFundPlan.find(
                      (fund) => Number(fund?.id) === Number(scheme?.id)
                    ) as unknown as FundProps
                  ).plans,
                  application.plan,
                ],
              } as unknown as FundProps)
            : updatedFundPlan.find((fund) => Number(fund?.id) === Number(scheme?.id)) || null;
        setSelectedFund(updatePlans);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, [application]);

  const getClassPlanOptionsOrDetails = (isAccreditedInvestor: boolean, detail?: string) => {
    if (detail) {
      return selectedFund?.[detail] || '';
    }

    const plansCombinedWithApplicationPlan = selectedFund?.plans?.some(
      (plan) => Number(plan?.id) === Number(application?.planId)
    )
      ? selectedFund?.plans
      : application?.schemeId?.toString() === selectedFund?.id?.toString()
      ? selectedFund?.plans?.concat(application?.plan as unknown as ClassPlanProps)
      : selectedFund?.plans;
    const filteredPlans = (
      isAccreditedInvestor
        ? plansCombinedWithApplicationPlan?.filter((plan) =>
            isAccreditedInvestorApplicable(plan?.accreditedInvestorApplicable)
          )
        : plansCombinedWithApplicationPlan
    )?.filter((ele) => ele);
    return (
      filteredPlans?.map((plan) => ({
        key: plan?.planDescription,
        value: Number(plan?.id),
      })) || []
    );
  };

  useEffect(() => {
    (async function () {
      try {
        setJointHoldersData(jointHolderRelations);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [jointHolderRelations]);

  const isFieldDisabled = [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER].includes(role);
  return (
    <Formik
      initialValues={paymentDetails}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={(values: Values) => {
        const minCommitmentAmountBasedOnJointHolders =
          application && application.applicants?.length > 1 && jointHoldersData
            ? (updatedCommitmentAmount(
                Number(minCommitmentAmount),
                application.applicants,
                jointHoldersData
              ) as number)
            : minCommitmentAmount;
        try {
          validateYupSchema(
            values,
            investmentPaymentDetailsSchema(
              (values?.isAccreditedInvestor
                ? selectedPlan?.accreditedMinCommitmentAmount || 0
                : minCommitmentAmountBasedOnJointHolders) as number,
              maxCommitmentAmount || 0,
              minContributionPercentage || 0,
              currency || '',
              values?.saveType,
              maxExitCharge === 0 || maxExitCharge ? Number(maxExitCharge) : 100,
              maxHurdleRate === 0 || maxHurdleRate ? Number(maxHurdleRate) : 100,
              maxManagementFee === 0 || maxManagementFee ? Number(maxManagementFee) : 100,
              maxPerformanceFee === 0 || maxPerformanceFee ? Number(maxPerformanceFee) : 100,
              maxPlacementFee === 0 || maxPlacementFee ? Number(maxPlacementFee) : 100,
              maxOperatingExpense === 0 || maxOperatingExpense ? Number(maxOperatingExpense) : 100,
              maxSetupFee === 0 || maxSetupFee ? Number(maxSetupFee) : 100,
              maxAdditionalReturn === 0 || maxAdditionalReturn ? Number(maxAdditionalReturn) : 100,
              minExitCharge,
              minHurdleRate,
              minManagementFee,
              minPerformanceFee,
              minPlacementFee,
              minOperatingExpense,
              minSetupFee,
              minAdditionalReturn,
              minCarryShare,
              maxCarryShare === 0 || maxCarryShare ? Number(maxCarryShare) : 100
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}>
      {({ handleSubmit, values, setValues, touched, setTouched }) => (
        <Grid
          container
          rowSpacing={1}
          columnSpacing={5}
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}>
          <Grid item xs={12}>
            <MFCheckbox
              name={`isAccreditedInvestor`}
              label="Accredited Investor"
              sx={{ mt: 1 }}
              onChange={({ target: { checked } }) => {
                setValues({
                  ...values,
                  ...fundInitialValues,
                  isAccreditedInvestor: checked,
                });
                setTouched({
                  ...touched,
                  ...removeErrors(),
                });
                setSelectedFund(null);
                setSelectedPlan(null);
              }}
              disabled={isFieldDisabled}
            />
          </Grid>

          <Grid item xs={12}>
            <SearchableSelect
              name={'schemeId'}
              label="Fund (Scheme) *"
              items={fundsList.map((fund) => ({ value: Number(fund?.id), key: fund.schemeName }))}
              onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                setValues({
                  ...values,
                  ...fundInitialValues,
                  schemeId: value as string,
                  setupFeeAmount: 0,
                  setupFeeGST: 0,
                  totalSetupFee: 0,
                  setupFeeTDS: 0,
                  netSetupFeeLessTDS: 0,
                  isAccreditedInvestor: values.isAccreditedInvestor,
                  totalAmountPayable: 0,
                });
                setTouched({
                  ...touched,
                  ...removeErrors(),
                  isAccreditedInvestor: touched.isAccreditedInvestor,
                });
                setSelectedFund(fundsList.find((f) => Number(f?.id) === value) || null);
                setSelectedPlan(null);
              }}
              disabled={isFieldDisabled || false}
              searchFieldPlaceholder={'Search Fund (Scheme)'}
            />
          </Grid>
          <InvestmentDetails
            values={values}
            setSelectedPlan={setSelectedPlan}
            setValues={setValues}
            isFieldDisabled={isFieldDisabled}
            getClassPlanOptionsOrDetails={getClassPlanOptionsOrDetails}
            selectedFund={selectedFund}
            selectedPlan={selectedPlan}
          />
          <FieldValidationNote />
          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Grid>
      )}
    </Formik>
  );
}
